import { Text, TextButton } from '@wix/design-system';
import { getSearchParams } from '@wix/package-picker-shared';
import { useLocalization } from '@wix/package-picker-shared-studio';
import { FeaturesGrid as PlatformFeaturesGrid } from '@wix/premium-purchase-platform/@components';
import {
  CreativeOffer,
  PurchaseOffering,
} from '@wix/premium-purchase-platform/@sdk';
import classNames from 'classnames';
import React from 'react';
import { StudioPackagePickerModule } from '../../modules/studio-package-picker.module';
import styles from './FeaturesGrid.scss';
import { FeaturesGridFeatureRowValue } from './FeaturesGridFeatureRowValue';
import { FeaturesGridNewBadge } from './FeaturesGridNewBadge';

const AI_CREDITS_TOOLTIP_KEY =
  'premium-purchase-plan.feature_table.ai_credits_section.ai_credits_tooltip';
const AI_CREDITS_DISCLAIMER_KEY =
  'premium-purchase-plan.package_picker.disclaimer.ai_credits_redesign';
const AI_CREDITS_URL = 'https://support.wix.com/article/about-ai-credits';
const AI_CREDITS_LINK_COLOR = 'var(--wds-color-text-primary)';

interface FeaturesGridFeatureRowProps {
  feature: CreativeOffer.GridFeature;
  products?: PurchaseOffering.PurchaseProduct[];
}

export const FeaturesGridFeatureRow = ({
  feature,
  products,
}: FeaturesGridFeatureRowProps) => {
  const { t, Trans } = StudioPackagePickerModule.internals.useTranslations();
  const { domainName } = getSearchParams();
  const { shouldShowJapaneseExperience } = useLocalization();
  const { experiments } =
    StudioPackagePickerModule.internals.experiments.hooks.useExperiments();

  if (!feature.content?.name?.translationKey || feature.metadata?.hidden) {
    return null;
  }

  const showNewBadge =
    experiments.isEnabled('ppNewFeaturesSep26') &&
    feature.metadata?.isNew === 'true';

  const showAiCreditsDisclaimer =
    feature.content?.tooltip?.translationKey === AI_CREDITS_TOOLTIP_KEY;

  const translationValues = { domainName };
  const translationTags = {
    url: (
      <TextButton
        as="a"
        target="_blank"
        underline="onHover"
        size={shouldShowJapaneseExperience ? 'tiny' : 'small'}
        href={t(feature.metadata?.tooltipUrlKey ?? '')}
        onClick={() => {
          StudioPackagePickerModule.internals.bi.onFeatureTooltipLinkClick?.({
            featureKey: feature.content?.name?.translationKey,
            url: t(feature.metadata?.tooltipUrlKey ?? ''),
          });
        }}
      />
    ),
  };

  return (
    <PlatformFeaturesGrid.Content.FeatureRow.Root
      size={shouldShowJapaneseExperience ? 'medium' : 'small'}
    >
      <PlatformFeaturesGrid.Content.FeatureRow.Cell>
        <PlatformFeaturesGrid.Content.FeatureRow.Header
          nameWeight={shouldShowJapaneseExperience ? 'bold' : 'normal'}
          name={
            <Trans
              i18nKey={feature.content?.name?.translationKey ?? ''}
              values={translationValues}
              components={translationTags}
            />
          }
          descriptionAsSubtitle={shouldShowJapaneseExperience}
          tooltip={
            <Text
              size="small"
              className={classNames(styles.featureRowDescription, {
                [styles.japan]: shouldShowJapaneseExperience,
              })}
              skin="standard"
              secondary={shouldShowJapaneseExperience}
            >
              <Trans
                i18nKey={feature.content?.tooltip?.translationKey ?? ''}
                values={translationValues}
                components={translationTags}
              />
              {showAiCreditsDisclaimer && (
                <>
                  <br />
                  <br />
                  <Text size="tiny" secondary>
                    <Trans
                      i18nKey={AI_CREDITS_DISCLAIMER_KEY}
                      components={{
                        1: (
                          // eslint-disable-next-line jsx-a11y/anchor-has-content -- content is injected by <Trans>
                          <a
                            href={AI_CREDITS_URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            ref={(el: HTMLAnchorElement | null) =>
                              el?.style.setProperty(
                                'color',
                                AI_CREDITS_LINK_COLOR,
                                'important',
                              )
                            }
                          />
                        ),
                      }}
                    />
                  </Text>
                </>
              )}
            </Text>
          }
          badge={
            showNewBadge ? (
              <FeaturesGridNewBadge dataHook="features-grid-new-badge" />
            ) : undefined
          }
          onTooltipViewed={() => {
            StudioPackagePickerModule.internals.bi.onFeatureTooltipHover?.({
              featureKey: feature.content?.name?.translationKey,
            });
          }}
        />
      </PlatformFeaturesGrid.Content.FeatureRow.Cell>
      {products?.map((product) => (
        <PlatformFeaturesGrid.Content.FeatureRow.Cell
          key={`${feature.id}-${product.productId}`}
        >
          <FeaturesGridFeatureRowValue
            comparisonValue={feature.values?.[product.productId ?? '']}
          />
        </PlatformFeaturesGrid.Content.FeatureRow.Cell>
      ))}
    </PlatformFeaturesGrid.Content.FeatureRow.Root>
  );
};
